'use client';

import { useState, useEffect, useCallback, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/components/auth-provider';
import { useTranslation } from '@/components/i18n-provider';
import Link from 'next/link';
import { User, Bell, Shield, Palette, LogOut, Check, Globe, Link2, Plus, RefreshCw, Trash2, ExternalLink, Copy, CheckCircle2, Clock, XCircle, AlertCircle, LinkIcon, Chrome, Code, Key, BookOpen, ChevronRight, ChevronDown, Settings, Camera, Save, Fingerprint, Lock, Volume2, Sun, Moon, Monitor, type LucideIcon } from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { Spinner } from '@/components/ui/spinner';
import { Avatar } from '@/components/avatar';
import { openCookiePreferences } from '@/components/cookie-consent-banner';
import { cn } from '@/lib/utils';
import { formatCredits } from '@/lib/balance/units';
import { AddProviderKeyDialog } from '@/components/add-provider-key-dialog';
import { useTheme } from '@/components/theme-provider';
import { useRailAutohide } from '@/lib/workspaces/rail-flag';
import { isGuestUser } from '@/lib/auth/is-guest';
import { PanelBackground } from '@/components/panel-bg';
import ConnectedAccountsTab, { type ConnectedApp } from '@/components/connected-accounts-tab';
import { usePushNotifications } from '@/components/sw-registration';
import { Tip } from '@/components/ui/tooltip';
import { DomainAppBindings } from '@/components/settings/domain-app-bindings';
import { EmailChange } from '@/components/settings/email-change';
import { BrowserAssistantAccess } from '@/components/settings/browser-assistant-access';

/**
 * Settings controls. One row anatomy everywhere — label + explanation on the
 * left, the control on the right — so a settings page reads as one surface
 * instead of a pile of ad-hoc inputs. Kept local to this view; promote to
 * components/ui if a second page needs them.
 */
function SettingRow({
  label,
  description,
  control,
  htmlFor,
}: {
  label: string;
  description?: string;
  control: React.ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-6 px-4 py-3">
      <div className="min-w-0">
        <label
          htmlFor={htmlFor}
          className="block text-sm font-medium text-foreground"
        >
          {label}
        </label>
        {description && (
          <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

function SettingSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {/* Rows live on one grouped surface, divided — so a section reads as a
          block instead of running into the next one's label. */}
      <div className="divide-y divide-border/50 overflow-hidden rounded-xl border border-border/60">
        {children}
      </div>
    </section>
  );
}

function Toggle({
  checked,
  onChange,
  id,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  id?: string;
  label: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        checked ? 'bg-primary' : 'bg-foreground/15',
      )}
    >
      <span
        className={cn(
          'inline-block h-4.5 w-4.5 transform rounded-full bg-background shadow-sm transition-transform',
          checked ? 'translate-x-[1.4rem]' : 'translate-x-1',
        )}
        style={{ width: '1.125rem', height: '1.125rem' }}
      />
    </button>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string; icon?: LucideIcon }>;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex gap-0.5 rounded-lg bg-foreground/[0.05] p-0.5"
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-all',
              active
                ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.icon && <o.icon className="h-3.5 w-3.5" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// X (Twitter) logo icon
function XIcon({ className }: { className?: string }) {
  return (
    <svg 
      viewBox="0 0 24 24" 
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Labels for the email categories the server sends us. The list of categories
// itself comes from the API, so a new one appears here even before it has copy:
// it falls back to its own name rather than an untranslated key.
const EMAIL_CATEGORY_KEYS: Record<string, { title: string; description: string }> = {
  money: { title: 'settings.emailCategoryMoney', description: 'settings.emailCategoryMoneyDesc' },
  legal: { title: 'settings.emailCategoryLegal', description: 'settings.emailCategoryLegalDesc' },
  product: { title: 'settings.emailCategoryProduct', description: 'settings.emailCategoryProductDesc' },
  // `marketing` was missing, so the fallback below rendered a bare lowercase
  // "marketing" with no description — an unlabelled consent toggle on the one
  // screen where that is a legal problem, not a cosmetic one.
  marketing: { title: 'settings.emailCategoryMarketing', description: 'settings.emailCategoryMarketingDesc' },
};

function emailCategoryTitle(t: (key: string) => string, category: string): string {
  const keys = EMAIL_CATEGORY_KEYS[category];
  return keys ? t(keys.title) : category;
}

function emailCategoryDescription(t: (key: string) => string, category: string): string {
  const keys = EMAIL_CATEGORY_KEYS[category];
  return keys ? t(keys.description) : '';
}

// OAuth Apps card for Developer tab
function OAuthAppsCard() {
  const { t } = useTranslation();
  const [apps, setApps] = useState<{ id: string; clientId: string; clientName: string; redirectUris: string[]; description: string | null; createdAt: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/oauth/clients')
      .then(r => r.json())
      .then(data => setApps(data.clients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!name.trim() || !redirectUri.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/oauth/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: name.trim(),
          redirectUris: redirectUri.split(',').map(u => u.trim()).filter(Boolean),
          description: description.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || t('settings.failedToCreateApp')); return; }
      setApps(prev => [...prev, data.client]);
      setNewSecret({ clientId: data.client.clientId, clientSecret: data.client.clientSecret });
      setFormOpen(false);
      setName('');
      setRedirectUri('');
      setDescription('');
    } catch { setError(t('settings.somethingWentWrong')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('settings.deleteOauthConfirm'))) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/oauth/clients/${id}`, { method: 'DELETE' });
      if (res.ok) setApps(prev => prev.filter(a => a.id !== id));
    } finally { setDeletingId(null); }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="card p-4 @sm/main:p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold">{t('settings.oauthAppsTitle')}</h3>
        {!formOpen && (
          <button onClick={() => setFormOpen(true)} className="btn-primary flex items-center gap-1.5 text-sm">
            <Plus className="h-3.5 w-3.5" />
            {t('settings.createOauthApp')}
          </button>
        )}
      </div>

      {newSecret && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">{t('settings.oauthAppCreated')}</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0">{t('settings.clientId')}</span>
              <code className="flex-1 bg-background px-2 py-1 rounded text-xs font-mono truncate">{newSecret.clientId}</code>
              <button onClick={() => copyToClipboard(newSecret.clientId, 'id')} className="btn-secondary p-1.5">
                {copiedField === 'id' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0">{t('settings.clientSecret')}</span>
              <code className="flex-1 bg-background px-2 py-1 rounded text-xs font-mono truncate">{newSecret.clientSecret}</code>
              <button onClick={() => copyToClipboard(newSecret.clientSecret, 'secret')} className="btn-secondary p-1.5">
                {copiedField === 'secret' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
          </div>
          <button onClick={() => setNewSecret(null)} className="text-xs text-muted-foreground mt-2 hover:underline">{t('actions.dismiss')}</button>
        </div>
      )}

      {formOpen && (
        <div className="border border-border rounded-lg p-4 mb-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('settings.appName')}</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input w-full text-sm mt-1" placeholder={t('settings.appNamePlaceholder')} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('settings.redirectUris')}</label>
            <input value={redirectUri} onChange={e => setRedirectUri(e.target.value)} className="input w-full text-sm mt-1" placeholder={t('settings.redirectUrisPlaceholder')} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('settings.descriptionOptional')}</label>
            <input value={description} onChange={e => setDescription(e.target.value)} className="input w-full text-sm mt-1" placeholder={t('settings.descriptionPlaceholder')} />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={saving || !name.trim() || !redirectUri.trim()} className="btn-primary text-sm">
              {saving ? t('settings.creatingOauthApp') : t('settings.createOauthApp')}
            </button>
            <button onClick={() => { setFormOpen(false); setError(null); }} className="btn-secondary text-sm">{t('actions.cancel')}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><Spinner size="lg" tone="muted" /></div>
      ) : apps.length === 0 && !formOpen ? (
        <p className="text-sm text-muted-foreground">{t('settings.noOauthApps')}</p>
      ) : (
        <div className="space-y-2">
          {apps.map(app => (
            <div key={app.id} className="border border-border rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <span className="font-medium text-sm">{app.clientName}</span>
                  {app.description && <span className="text-xs text-muted-foreground ml-2">{app.description}</span>}
                </div>
                <button
                  onClick={() => handleDelete(app.id)}
                  disabled={deletingId === app.id}
                  className="text-destructive hover:bg-destructive/10 p-1.5 rounded shrink-0"
                >
                  {deletingId === app.id ? <Spinner size="sm" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>
              <div className="mt-1.5 text-xs text-muted-foreground space-y-0.5">
                <div>{t('settings.clientId')}: <code className="font-mono">{app.clientId}</code></div>
                <div>{t('settings.redirectLabel')}<code className="font-mono">{app.redirectUris.join(', ')}</code></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Types for domain verification
interface DomainVerification {
  id: string;
  domain: string;
  method: 'dns' | 'meta' | 'manual';
  status: 'pending' | 'verified' | 'failed' | 'expired';
  verificationCode?: string;
  channel?: { id: string; name: string; slug: string } | null;
  /** Verified domains only: does its DNS resolve to us? null = unknown, say nothing. */
  routed?: boolean | null;
  /**
   * A named app host in the platform's own zone (`<label>.robutler.app`),
   * issued by the operator for a published app. Read-only here: no delete,
   * no DNS records, no binding edits (the API refuses them too).
   */
  platformZone?: boolean;
  verifiedAt?: string;
  expiresAt?: string;
  createdAt: string;
}

/** The records to point a domain here, from GET /api/domains (env-configured). */
interface DomainDnsTargets {
  aRecord: string | null;
  cnameTarget: string | null;
}


// Types for API keys
interface ApiKey {
  id: string;
  name: string;
  provider: string;
  keyPrefix: string;
  isActive: boolean;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  paymentLimitPerToken?: string | null;
  paymentLimitDaily?: string | null;
}

// ConnectedApp type imported from connected-accounts-tab

function SettingsContent({
  embedded = false,
  initialTab,
}: { embedded?: boolean; initialTab?: string } = {}) {
  const { user, nsfwMode: currentNsfwMode, refresh, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  // Personal, per-device layout preference (localStorage + window event), so
  // the rail and this toggle stay in sync without a reload or a save button.
  const [railAutohide, setRailAutohide] = useRailAutohide();
  const { locale, setLocale, supportedLocales, localeNames } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    // Prefer the explicitly-passed tab (embedded dialog); fall back to the URL
    // for the standalone page. See the `initialTab` note on the component.
    let tab: string | null = initialTab ?? null;
    if (!tab && typeof window !== 'undefined') {
      tab = new URLSearchParams(window.location.search).get('tab');
    }
    if (tab === 'accounts' || tab === 'accounts_mcp') tab = 'integrations';
    else if (tab === 'apps' || tab === 'tokens') tab = 'developer';
    // The Agents tab is hidden; old links would land on an empty panel.
    else if (tab === 'agents') tab = 'profile';
    // Same for the AI Models (BYOK) tab.
    else if (tab === 'ai_models') tab = 'profile';
    return tab || 'profile';
  });
  const [navOpen, setNavOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // X settings state
  const [showXSettings, setShowXSettings] = useState(false);
  const [xSyncing, setXSyncing] = useState(false);

  // Username change state
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<{
    checking: boolean;
    valid?: boolean;
    available?: boolean;
    reservedOnX?: boolean;
    error?: string;
    warnings?: string[];
    hasAgentDependencies?: boolean;
    agentCount?: number;
    descendantCount?: number;
    currentUsername?: string | null;
  }>({ checking: false });
  const [usernameConfirmOpen, setUsernameConfirmOpen] = useState(false);
  const [changingUsername, setChangingUsername] = useState(false);
  
  // Profile form state
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [mentionNotifications, setMentionNotifications] = useState(true);
  const [replyNotifications, setReplyNotifications] = useState(true);
  const [pushPermissionState, setPushPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'unsupported'>('prompt');

  // Per-category email preferences. Both category lists come from the API so
  // this page never holds a second copy of what the mail system can send.
  const [emailPreferences, setEmailPreferences] = useState<Record<string, boolean>>({});
  const [optionalEmailCategories, setOptionalEmailCategories] = useState<string[]>([]);
  const [essentialEmailCategories, setEssentialEmailCategories] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const { requestPermission, subscribeToPush, unsubscribeFromPush, isSubscribed } = usePushNotifications();
  
  // Privacy settings
  const [profilePublic, setProfilePublic] = useState(true);
  const [showOnlineStatus, setShowOnlineStatus] = useState(true);
  const [dmPolicy, setDmPolicy] = useState<'everyone' | 'following' | 'nobody'>('everyone');
  const [nsfwMode, setNsfwMode] = useState<'mask' | 'filter' | 'allow'>(currentNsfwMode);
  const [nsfwSaving, setNsfwSaving] = useState(false);
  
  // Domain verification state
  const [domainVerifications, setDomainVerifications] = useState<DomainVerification[]>([]);
  const [domainDns, setDomainDns] = useState<DomainDnsTargets>({ aRecord: null, cnameTarget: null });
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<'dns' | 'meta'>('dns');
  const [addingDomain, setAddingDomain] = useState(false);
  const [verifyingDomainId, setVerifyingDomainId] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  
  
  // Voice / AI settings
  const [voiceSilenceThreshold, setVoiceSilenceThreshold] = useState(0.03);
  const [voiceSilenceDuration, setVoiceSilenceDuration] = useState(2000);
  const [voiceMinRecording, setVoiceMinRecording] = useState(1000);
  const [voiceAutoSend, setVoiceAutoSend] = useState(true);
  const [voiceTrimSilence, setVoiceTrimSilence] = useState(true);

  // BYOK substitution
  const [allowByokSubstitution, setAllowByokSubstitution] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState<string>('never');
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);
  const [showAddProviderKey, setShowAddProviderKey] = useState(false);
  const [tokenFilter, setTokenFilter] = useState<'active' | 'all'>('active');
  const [tokenPage, setTokenPage] = useState(0);
  const TOKENS_PER_PAGE = 10;
  const [editingTokenId, setEditingTokenId] = useState<string | null>(null);
  const [editTokenDaily, setEditTokenDaily] = useState('');
  const [editTokenTotal, setEditTokenTotal] = useState('');
  const [savingTokenLimits, setSavingTokenLimits] = useState(false);
  const [confirmRevokeTokenId, setConfirmRevokeTokenId] = useState<string | null>(null);

  // Connected Apps (OAuth) state
  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([]);

  // My Agents state (actual agent accounts, not tokens)
  const [myAgents, setMyAgents] = useState<any[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [agentConfigs, setAgentConfigs] = useState<Record<string, any>>({});
  const [savingAgentConfig, setSavingAgentConfig] = useState<string | null>(null);


  const searchParams = useSearchParams();

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setBio(user.bio || '');
    }
  }, [user]);

  useEffect(() => {
    fetch('/api/settings').then(r => r.ok ? r.json() : null).then(d => {
      if (d && typeof d.allowByokSubstitution === 'boolean') setAllowByokSubstitution(d.allowByokSubstitution);
    }).catch(() => {});
  }, []);

  // Load voice settings from localStorage (fast) and API (authoritative)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('voiceSettings');
      if (stored) {
        const s = JSON.parse(stored);
        if (s.silenceThreshold != null) setVoiceSilenceThreshold(s.silenceThreshold);
        if (s.silenceDuration != null) setVoiceSilenceDuration(s.silenceDuration);
        if (s.minRecordingDuration != null) setVoiceMinRecording(s.minRecordingDuration);
        if (s.autoSend != null) setVoiceAutoSend(s.autoSend);
        if (s.trimSilence != null) setVoiceTrimSilence(s.trimSilence);
      }
    } catch {}
    // Also fetch from API for authoritative values
    fetch('/api/users/me/settings')
      .then((res) => res.json())
      .then((data) => {
        const s = data.settings;
        if (s?.voiceSilenceThreshold != null) setVoiceSilenceThreshold(s.voiceSilenceThreshold);
        if (s?.voiceSilenceDuration != null) setVoiceSilenceDuration(s.voiceSilenceDuration);
        if (s?.voiceMinRecording != null) setVoiceMinRecording(s.voiceMinRecording);
        if (s?.voiceAutoSend != null) setVoiceAutoSend(s.voiceAutoSend);
        if (s?.voiceTrimSilence != null) setVoiceTrimSilence(s.voiceTrimSilence);
        if (s?.emailNotifications != null) setEmailNotifications(s.emailNotifications);
        if (s?.pushNotifications != null) setPushNotifications(s.pushNotifications);
        if (s?.mentionNotifications != null) setMentionNotifications(s.mentionNotifications);
        if (s?.replyNotifications != null) setReplyNotifications(s.replyNotifications);
        if (s?.emailPreferences) setEmailPreferences(s.emailPreferences);
        // Privacy block. These three were never hydrated, so the panel
        // always painted its optimistic defaults and a Save re-wrote
        // whatever the person had actually chosen back to "on".
        if (s?.profilePublic != null) setProfilePublic(s.profilePublic);
        if (s?.showOnlineStatus != null) setShowOnlineStatus(s.showOnlineStatus);
        if (s?.dmPolicy != null) setDmPolicy(s.dmPolicy);
        if (data?.emailCategories) {
          setOptionalEmailCategories(data.emailCategories.optional ?? []);
          setEssentialEmailCategories(data.emailCategories.essential ?? []);
        }
        // Sync to localStorage
        localStorage.setItem('voiceSettings', JSON.stringify({
          silenceThreshold: s?.voiceSilenceThreshold ?? 0.03,
          silenceDuration: s?.voiceSilenceDuration ?? 2000,
          minRecordingDuration: s?.voiceMinRecording ?? 1000,
          autoSend: s?.voiceAutoSend ?? true,
          trimSilence: s?.voiceTrimSilence ?? true,
        }));
      })
      .catch(() => {});
  }, []);
  
  // Fetch domain verifications
  const fetchDomainVerifications = useCallback(async () => {
    if (!user) return;
    setLoadingDomains(true);
    try {
      const res = await fetch('/api/domains');
      if (res.ok) {
        const data = await res.json();
        setDomainVerifications(data.verifications || []);
        setDomainDns(data.dns ?? { aRecord: null, cnameTarget: null });
      }
    } catch (error) {
      console.error('Error fetching domain verifications:', error);
    } finally {
      setLoadingDomains(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (activeTab === 'domains') {
      fetchDomainVerifications();
    }
  }, [activeTab, fetchDomainVerifications]);
  
  
  // Fetch access tokens (unified: api_keys, oauth_apps, agents, providers)
  const fetchApiKeys = useCallback(async () => {
    if (!user) return;
    setLoadingApiKeys(true);
    try {
      const res = await fetch('/api/access-tokens');
      if (res.ok) {
        const data = await res.json();
        const tokens = data.tokens || [];
        setApiKeys(tokens);
        // Deduplicate connected apps: keep latest active (non-revoked) token per clientId
        const oauthTokens = tokens.filter((t: any) => t.type === 'oauth_app' && !t.revokedAt && t.isActive !== false);
        const byClient = new Map<string, any>();
        for (const tok of oauthTokens) {
          const key = tok.clientId || tok.id;
          const existing = byClient.get(key);
          if (!existing || new Date(tok.createdAt) > new Date(existing.createdAt)) {
            byClient.set(key, {
              ...tok,
              limitDaily: tok.spendingLimitDaily ?? tok.limitDaily ?? null,
              limitTotal: tok.spendingLimitTotal ?? tok.limitTotal ?? null,
              spentToday: oauthTokens
                .filter((t: any) => (t.clientId || t.id) === key)
                .reduce((sum: number, t: any) => sum + Number(t.spentToday || 0), 0)
                .toString(),
              spentTotal: oauthTokens
                .filter((t: any) => (t.clientId || t.id) === key)
                .reduce((sum: number, t: any) => sum + Number(t.spentTotal || 0), 0)
                .toString(),
            });
          }
        }
        setConnectedApps(Array.from(byClient.values()));
      }
    } catch (error) {
      console.error('Error fetching access tokens:', error);
    } finally {
      setLoadingApiKeys(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (['ai_models', 'integrations', 'developer', 'agents'].includes(activeTab)) {
      fetchApiKeys();
    }
  }, [activeTab, fetchApiKeys]);

  // Fetch actual agent accounts when agents tab is active
  useEffect(() => {
    if (activeTab !== 'agents' || !user) return;
    setLoadingAgents(true);
    fetch('/api/agents')
      .then((r) => r.json())
      .then((data) => setMyAgents(data.agents ?? []))
      .catch(() => setMyAgents([]))
      .finally(() => setLoadingAgents(false));
  }, [activeTab, user]);


  // Auto-expand agent from URL search param. Inert while the Agents tab is
  // hidden: the fetch that fills `myAgents` is gated on that tab, so this
  // never fires. Restore alongside the tab (it would otherwise switch to a
  // tab that renders nothing).
  // useEffect(() => {
  //   const agentParam = searchParams.get('agent');
  //   if (agentParam && myAgents.length > 0) {
  //     const match = myAgents.find((a: any) => a.username === agentParam);
  //     if (match) {
  //       setExpandedAgent(agentParam);
  //       setActiveTab('agents');
  //       const config = match.agentConfig ?? {};
  //       setAgentConfigs((prev) => ({ ...prev, [agentParam]: { ...config } }));
  //     }
  //   }
  // }, [searchParams, myAgents]);

  // Initialize agent config when expanding
  const toggleAgentExpanded = (agent: any) => {
    const username = agent.username;
    if (expandedAgent === username) {
      setExpandedAgent(null);
    } else {
      setExpandedAgent(username);
      if (!agentConfigs[username]) {
        const config = agent.agentConfig ?? {};
        setAgentConfigs((prev) => ({ ...prev, [username]: { ...config } }));
      }
    }
  };

  const updateAgentConfig = (username: string, field: string, value: any) => {
    setAgentConfigs((prev) => ({
      ...prev,
      [username]: { ...(prev[username] ?? {}), [field]: value },
    }));
  };

  const handleSaveAgentConfig = async (username: string) => {
    setSavingAgentConfig(username);
    try {
      const config = agentConfigs[username] ?? {};
      const res = await fetch(`/api/agents/${username}/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        const updated = await res.json();
        setMyAgents((prev) =>
          prev.map((a: any) =>
            a.username === username ? { ...a, agentConfig: updated.config ?? config } : a
          )
        );
      }
    } catch (error) {
      console.error('Error saving agent config:', error);
    } finally {
      setSavingAgentConfig(null);
    }
  };


  const { t } = useTranslation();

  useEffect(() => {
    if (activeTab !== 'notifications') return;
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setPushPermissionState('unsupported');
      return;
    }
    setPushPermissionState(Notification.permission as 'prompt' | 'granted' | 'denied');
  }, [activeTab]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <User className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">{t('settings.title')}</h1>
        <p className="text-muted-foreground mb-6">
          {t('settings.signInRequired')}
        </p>
        <Link href="/login" className="btn-primary">
          {t('auth.signIn')}
        </Link>
      </div>
    );
  }

  const saveAvatarUrl = async (url: string | null) => {
    const res = await fetch('/api/users/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ avatarUrl: url }),
    });
    if (res.ok && refresh) refresh();
    return res.ok;
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('settings.uploadImageTypeError'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('settings.uploadImageSizeError'));
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setUploadingAvatar(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'avatar');

      const uploadRes = await fetch('/api/upload', { method: 'POST', body: formData });
      if (uploadRes.ok) {
        const data = await uploadRes.json();
        const saved = await saveAvatarUrl(data.url);
        if (saved) {
          setAvatarPreview(null);
        } else {
          setAvatarPreview(null);
          alert(t('settings.failedToSaveAvatar'));
        }
      } else {
        setAvatarPreview(null);
        alert(t('settings.failedToUploadAvatar'));
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setAvatarPreview(null);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setUploadingAvatar(true);
    try {
      const saved = await saveAvatarUrl(null);
      if (saved) {
        setAvatarPreview(null);
      }
    } catch (error) {
      console.error('Error removing avatar:', error);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName, bio }),
      });
      
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (refresh) refresh();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePush = async (enabled: boolean) => {
    if (enabled) {
      const granted = await requestPermission();
      if (!granted) {
        setPushPermissionState('denied');
        setPushNotifications(false);
        return;
      }
      setPushPermissionState('granted');
      const sub = await subscribeToPush();
      const subscribed = sub !== null;
      setPushNotifications(subscribed);
      if (subscribed) {
        await fetch('/api/users/me/settings', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pushNotifications: true }),
        });
      }
    } else {
      await unsubscribeFromPush();
      setPushNotifications(false);
      await fetch('/api/users/me/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pushNotifications: false }),
      });
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    setEmailError(null);
    try {
      const res = await fetch('/api/users/me/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailNotifications,
          pushNotifications,
          mentionNotifications,
          replyNotifications,
          emailPreferences,
        }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.settings?.emailPreferences) setEmailPreferences(data.settings.emailPreferences);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        // Say so rather than leaving a switch showing a state we did not store.
        setEmailError(t('settings.emailSaveFailed'));
      }
    } catch (error) {
      console.error('Error saving notifications:', error);
      setEmailError(t('settings.emailSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrivacy = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/users/me/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profilePublic,
          showOnlineStatus,
          dmPolicy,
        }),
      });
      
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving privacy:', error);
    } finally {
      setSaving(false);
    }
  };

  // Flat sidebar with dividers (no group labels)
  const tabs: ({ id: string; labelKey: string; icon: typeof User } | 'divider')[] = [
    { id: 'profile', labelKey: 'settings.profile', icon: User },
    { id: 'integrations', labelKey: 'settings.accountsAndMcp', icon: LinkIcon },
    // AI MODELS (BYOK) tab hidden: the surface for adding and managing your own
    // LLM provider keys is not being offered right now. Nothing behind it was
    // removed — the APIs still accept provider keys and any key already stored
    // keeps working. Restore this entry, the `?tab=ai_models` redirect, and the
    // content block below to bring it back.
    // { id: 'ai_models', labelKey: 'settings.aiKeys', icon: Key },
    { id: 'privacy', labelKey: 'settings.privacy', icon: Shield },
    'divider',
    // AGENTS tab hidden: its only remaining content was a heading and a link
    // to the CLI docs (the per-agent config UI it used to host was removed
    // earlier — the state/handlers below it are already unreferenced). Agents
    // are managed from the workspace Agents view. Restore this entry, the
    // `?tab=agents` redirect, and the content block below to bring it back.
    // { id: 'agents', labelKey: 'settings.agents', icon: Bot },
    // 'divider',
    { id: 'appearance', labelKey: 'settings.appearance', icon: Palette },
    { id: 'language', labelKey: 'settings.language', icon: Globe },
    // Loudspeaker, not a bell: Bell belongs to Notifications and the two tabs
    // sat adjacent with the same icon.
    { id: 'voice_audio', labelKey: 'settings.voiceAudio', icon: Volume2 },
    { id: 'notifications', labelKey: 'settings.notifications', icon: Bell },
    { id: 'security', labelKey: 'settings.biometrics', icon: Fingerprint },
    'divider',
    { id: 'domains', labelKey: 'settings.domains', icon: Chrome },
    'divider',
    { id: 'developer', labelKey: 'settings.developer', icon: Code },
  ];
  
  // Domain verification handlers
  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    setDomainError(null);
    setAddingDomain(true);
    
    try {
      const res = await fetch('/api/domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain, method: verificationMethod }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        setDomainError(data.error || t('settings.failedToInitiateVerification'));
        return;
      }
      
      // Add the new verification to the list
      setDomainVerifications(prev => [data.verification, ...prev]);
      setNewDomain('');
      setShowAddDomain(false);
    } catch (error) {
      setDomainError(t('settings.failedToAddDomain'));
    } finally {
      setAddingDomain(false);
    }
  };
  
  const handleVerifyDomain = async (verificationId: string) => {
    setVerifyingDomainId(verificationId);
    setDomainError(null);
    
    try {
      const res = await fetch(`/api/domains/${verificationId}/check`, {
        method: 'POST',
      });
      
      const data = await res.json();
      
      if (data.success && data.status === 'verified') {
        // Update the verification in the list
        setDomainVerifications(prev => 
          prev.map(v => v.id === verificationId 
            ? { ...v, status: 'verified' as const, channel: data.channel, verifiedAt: new Date().toISOString() }
            : v
          )
        );
      } else {
        setDomainError(data.error || t('settings.verificationFailed'));
      }
    } catch (error) {
      setDomainError(t('settings.failedToCheckVerification'));
    } finally {
      setVerifyingDomainId(null);
    }
  };
  
  const handleDeleteDomain = async (verificationId: string) => {
    if (!confirm(t('settings.confirmRemoveDomain'))) return;
    
    try {
      const res = await fetch(`/api/domains/${verificationId}`, {
        method: 'DELETE',
      });
      
      if (res.ok) {
        setDomainVerifications(prev => prev.filter(v => v.id !== verificationId));
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
    }
  };
  
  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // One DNS record row: `TYPE  HOST  VALUE` plus a copy button for the value.
  const renderDnsRow = (type: string, host: string, value: string, copyId: string) => (
    <div className="flex items-center gap-2">
      <code className="flex-1 bg-background px-3 py-2 rounded text-xs overflow-x-auto whitespace-nowrap">
        <span className="inline-block w-14 text-muted-foreground">{type}</span>
        <span className="inline-block w-10 text-muted-foreground">{host}</span>
        {value}
      </code>
      <button
        onClick={() => copyToClipboard(value, copyId)}
        className="btn-ghost text-xs flex items-center gap-1 flex-shrink-0"
      >
        {copiedCode === copyId ? (
          <>
            <Check className="h-3 w-3" />
            {t('settings.domainCopied')}
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" />
            {t('settings.domainCopy')}
          </>
        )}
      </button>
    </div>
  );

  // The hosting records for a domain. Verification (TXT) only proves
  // ownership; nothing serves until the domain's DNS points at the platform.
  // Shown while pending (so both records land in one DNS session) and again on
  // a verified domain whose DNS provably points elsewhere. A verified domain
  // that routes correctly gets a single confirmation line; when the targets are
  // not configured or the probe was inconclusive, say nothing rather than guess.
  const renderDnsRecords = (verification: DomainVerification) => {
    const { aRecord, cnameTarget } = domainDns;
    if (!aRecord && !cnameTarget) return null;
    if (verification.status === 'verified' && verification.routed === true) {
      return (
        <p className="text-xs text-muted-foreground mt-2">{t('settings.dnsRouted')}</p>
      );
    }
    const misrouted = verification.status === 'verified' && verification.routed === false;
    if (verification.status !== 'pending' && !misrouted) return null;
    return (
      <div className="bg-muted/50 rounded p-3 text-sm mt-3">
        <div
          className={`font-medium mb-2 ${
            misrouted ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground'
          }`}
        >
          {misrouted ? t('settings.dnsNotRouted') : t('settings.dnsRecordsTitle')}
        </div>
        <div className="space-y-2">
          {aRecord && renderDnsRow('A', '@', aRecord, `${verification.id}:a`)}
          {cnameTarget && renderDnsRow('CNAME', 'www', cnameTarget, `${verification.id}:cname`)}
        </div>
        <p className="text-xs text-muted-foreground mt-2">{t('settings.dnsApexHint')}</p>
      </div>
    );
  };


  // API Key handlers
  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    
    setCreatingKey(true);
    try {
      const expiresInDays = newKeyExpiry === 'never' ? undefined : parseInt(newKeyExpiry);
      const res = await fetch('/api/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName, expiresInDays }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewlyCreatedKey(data.rawKey);
        setApiKeys(prev => [data.key, ...prev]);
        setNewKeyName('');
        setNewKeyExpiry('never');
        setShowCreateKey(false);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    } finally {
      setCreatingKey(false);
    }
  };
  
  const handleRevokeApiKey = async (keyId: string) => {
    if (!confirm(t('settings.confirmRevokeApiKey'))) return;
    
    setRevokingKeyId(keyId);
    try {
      const res = await fetch(`/api/api-keys/${keyId}`, { method: 'DELETE' });
      if (res.ok) {
        setApiKeys(prev => prev.map(k => k.id === keyId ? { ...k, isActive: false } : k));
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
    } finally {
      setRevokingKeyId(null);
    }
  };
  
  const copyApiKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedApiKey(true);
      setTimeout(() => setCopiedApiKey(false), 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
    }
  };
  
  // Connected App handlers.
  //
  // 1 credit = 1e9 nanocredits (`SCALE`, lib/balance/units.ts). Until 2026-09-22
  // every conversion in this file divided by 1e11, and the limit editor below
  // multiplied by 1e11 on the way back: a person who typed "1.00" a day stored
  // a row at 100 credits a day, and the page showed it back as "1.00", so the
  // 100x was invisible from here. The access-token gate went live the same day
  // (S-208) and reads the row as it is, so the display now says what the gate
  // enforces. Stored rows are NOT rescaled here; that is the operator decision
  // recorded beside `DEFAULT_LIMITS` in lib/access-tokens/service.ts.
  const formatNanocredits = (nanocredits: string | null): string => {
    if (!nanocredits || nanocredits === '0') return t('settings.zeroCredits');
    const credits = Number(nanocredits) / 1e9;
    return credits >= 0.01
      ? `${credits.toFixed(2)} ${t('settings.creditsUnit')}`
      : `${credits.toFixed(6).replace(/0+$/, '')} ${t('settings.creditsUnit')}`;
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'expired': return <AlertCircle className="h-5 w-5 text-gray-500" />;
      default: return null;
    }
  };

  return (
    <div className={embedded ? 'w-full min-w-0' : 'w-full max-w-4xl mx-auto min-w-0'}>
      {!embedded && <PanelBackground />}
      {!embedded && <h1 className="text-2xl font-bold mb-6">{t('settings.title')}</h1>}

      <div className="flex flex-col @lg/main:flex-row gap-6">
        {/* Sidebar — full nav on desktop, dropdown overlay on mobile */}
        <nav className="@lg/main:w-48 shrink-0 relative">
          {/* Mobile dropdown trigger */}
          {(() => {
            const active = tabs.find(t => t !== 'divider' && t.id === activeTab) as { id: string; labelKey: string; icon: typeof User } | undefined;
            const ActiveIcon = active?.icon ?? Settings;
            return (
              <button
                onClick={() => setNavOpen(o => !o)}
                className="@lg/main:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border border-border bg-card shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" />
                  {active ? t(active.labelKey) : t('settings.title')}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${navOpen ? 'rotate-180' : ''}`} />
              </button>
            );
          })()}

          {/* Nav items — always visible on desktop, floating overlay on mobile */}
          <div className={`${navOpen ? 'block' : 'hidden'} @lg/main:block @lg/main:relative @lg/main:mt-0 @lg/main:shadow-none @lg/main:border-0 @lg/main:bg-transparent @lg/main:rounded-none @lg/main:max-h-none absolute left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg max-h-[60vh] overflow-y-auto space-y-0.5 p-1 @lg/main:p-0`}>
            {tabs.map((tab, i) => {
              if (tab === 'divider') {
                return <div key={`divider-${i}`} className="border-t border-border my-1" />;
              }
              const { id, labelKey, icon: Icon } = tab;
              return (
                <button
                  key={id}
                  onClick={() => { setActiveTab(id); setNavOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(labelKey)}
                </button>
              );
            })}
            <div className="border-t border-border my-1" />
            {/* A button, NOT a <Link>. `/api/auth/logout` exports only POST, so
                the link this used to be issued a GET, hit no handler, and left
                the session fully intact — Sign Out silently did nothing.
                `logout()` from the auth provider POSTs correctly, clears client
                state and redirects. */}
            <button
              type="button"
              onClick={() => { void logout(); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              {/* Guests have no account to sign out OF; logging out destroys
                  the guest session, so say that. */}
              {isGuestUser(user) ? t('auth.endGuestSession') : t('auth.signOut')}
            </button>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {activeTab === 'profile' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">{t('settings.profileSettings')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('settings.username')}</label>
                  {!editingUsername ? (
                    <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={`@${user.username}`}
                    disabled
                        className="input flex-1 bg-muted text-muted-foreground"
                      />
                      <button
                        onClick={() => {
                          setEditingUsername(true);
                          setNewUsername(user.username);
                          setUsernameStatus({ checking: false });
                        }}
                        className="btn-ghost text-xs text-muted-foreground hover:text-foreground"
                      >
                        {t('settings.changeUsernameBtn')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">@</span>
                        <input
                          type="text"
                          value={newUsername}
                          onChange={(e) => {
                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                            setNewUsername(val);
                            setUsernameStatus({ checking: false });
                          }}
                          className="input flex-1"
                          placeholder={t('settings.newUsernamePlaceholder')}
                          maxLength={30}
                        />
                        <button
                          onClick={async () => {
                            if (!newUsername || newUsername === user.username) return;
                            setUsernameStatus({ checking: true });
                            try {
                              const res = await fetch(`/api/user/username?check=${encodeURIComponent(newUsername)}`);
                              const data = await res.json();
                              setUsernameStatus({ checking: false, ...data });
                            } catch {
                              setUsernameStatus({ checking: false, error: t('settings.checkFailed') });
                            }
                          }}
                          disabled={!newUsername || newUsername === user.username || usernameStatus.checking}
                          className="btn-outline text-sm"
                        >
                          {usernameStatus.checking ? t('settings.checkingUsername') : t('settings.checkUsernameBtn')}
                        </button>
                      </div>

                      {/* Status feedback */}
                      {usernameStatus.error && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {usernameStatus.error}
                        </p>
                      )}
                      {usernameStatus.available && (
                        <p className="text-xs text-green-500 flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          {t('settings.usernameAvailable', { username: newUsername })}
                        </p>
                      )}
                      {/* Warnings and confirm — built client-side from the
                          API's counts so they localize like the rest of the
                          settings UI (the server strings are the raw-API
                          fallback and are English-only). */}
                      {usernameStatus.available && usernameStatus.warnings && (
                        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-1.5">
                          <p className="text-xs font-medium text-yellow-600">{t('settings.usernameChangeWarning')}</p>
                          {[
                            ...((usernameStatus.descendantCount ?? 0) > 0
                              ? [t('settings.usernameWarnCascade', {
                                  count: String(usernameStatus.descendantCount),
                                  current: usernameStatus.currentUsername ?? '',
                                  next: newUsername,
                                })]
                              : (usernameStatus.agentCount ?? 0) > 0
                                ? [t('settings.usernameWarnAgents', { count: String(usernameStatus.agentCount) })]
                                : []),
                            t('settings.usernameWarnReleased'),
                            t('settings.usernameWarnLinks'),
                          ].map((w: string, i: number) => (
                            <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                              <AlertCircle className="h-3 w-3 shrink-0 mt-0.5 text-yellow-500" />
                              {w}
                            </p>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        {usernameStatus.available && (
                          <button
                            onClick={async () => {
                              setChangingUsername(true);
                              try {
                                const res = await fetch('/api/user/username', {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ username: newUsername, confirmed: true }),
                                });
                                const data = await res.json();
                                if (data.success) {
                                  setEditingUsername(false);
                                  window.location.reload();
                                } else {
                                  setUsernameStatus(prev => ({ ...prev, error: data.error }));
                                }
                              } catch {
                                setUsernameStatus(prev => ({ ...prev, error: t('settings.failedToUpdateUsername') }));
                              } finally {
                                setChangingUsername(false);
                              }
                            }}
                            disabled={changingUsername}
                            className="btn-primary text-sm"
                          >
                            {changingUsername ? t('settings.changingUsername') : t('settings.confirmUsernameChange')}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingUsername(false);
                            setUsernameStatus({ checking: false });
                          }}
                          className="btn-ghost text-sm text-muted-foreground"
                        >
                          {t('actions.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-3">{t('settings.avatarLabel')}</label>
                  <div className="flex items-center gap-5">
                    <label className="relative cursor-pointer group shrink-0">
                      <Avatar
                        seed={user.username || user.displayName || 'user'}
                        type={user.accountType === 'agent' ? 'agent' : 'human'}
                        src={avatarPreview || user.avatarUrl}
                        className="w-24 h-24 ring-2 ring-border ring-offset-2 ring-offset-background transition-all group-hover:ring-primary/50"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all duration-200 rounded-full">
                        <div className="flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          {uploadingAvatar ? (
                            <Spinner size="lg" tone="onDark" />
                          ) : (
                            <>
                              <Camera className="h-5 w-5 text-white drop-shadow-sm" />
                              <span className="text-[10px] font-medium text-white drop-shadow-sm">{t('settings.editAvatar')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="hidden"
                        onChange={handleAvatarUpload}
                        disabled={uploadingAvatar}
                      />
                    </label>
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-muted-foreground">{t('settings.avatarHelp')}</p>
                      <p className="text-xs text-muted-foreground/60">{t('settings.avatarFormats')}</p>
                      {user.avatarUrl && (
                        <button
                          onClick={handleRemoveAvatar}
                          disabled={uploadingAvatar}
                          className="text-xs text-muted-foreground hover:text-destructive transition-colors text-left w-fit"
                        >
                          {uploadingAvatar ? t('settings.removingAvatar') : t('settings.removeCustomAvatar')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('settings.displayName')}</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input w-full"
                    placeholder={t('settings.displayNamePlaceholder')}
                  />
                </div>
                {/* Editable, with its own save: the server asks for a recent
                    sign-in before it changes the address (S-229 update and
                    S-232, 2026-09-24), and that step lives inside the field
                    rather than failing the whole profile save. */}
                <EmailChange />
                <div>
                  <label className="block text-sm font-medium mb-2">{t('settings.bio')}</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder={t('settings.bioPlaceholder')}
                    className="textarea w-full"
                    rows={3}
                    maxLength={200}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{bio.length}/200 {t('settings.characters')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('settings.accountType')}</label>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {/* lucide, not emoji: emoji render differently per platform
                        and font, and `Bot` is already the account-type icon used
                        elsewhere in this file. */}
                    {user.accountType === 'agent' ? (
                      <>
                        <Bot className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {t('settings.agent')}
                      </>
                    ) : (
                      <>
                        <User className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {t('settings.human')}
                      </>
                    )}
                  </div>
                </div>
                <button 
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? (
                    <Spinner size="md" />
                  ) : saved ? (
                    <Check className="h-4 w-4" />
                  ) : null}
                  {saved ? t('actions.saved') : t('actions.saveChanges')}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <ConnectedAccountsTab
              connectedApps={connectedApps}
              onRevokeApp={async (appId) => {
                const res = await fetch(`/api/oauth/apps/${appId}`, { method: 'DELETE' });
                if (res.ok) {
                  setConnectedApps((prev) => prev.filter((a) => a.id !== appId));
                } else {
                  console.error('Failed to revoke app:', await res.text().catch(() => res.status));
                }
              }}
              onSaveAppLimits={async (appId, dailyNano, totalNano) => {
                const res = await fetch(`/api/access-tokens/${appId}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ limitDaily: dailyNano, limitTotal: totalNano }),
                });
                if (res.ok) {
                  setConnectedApps((prev) =>
                    prev.map((a) => a.id === appId ? { ...a, limitDaily: dailyNano, limitTotal: totalNano } : a)
                  );
                }
              }}
              formatNanocredits={formatNanocredits}
            />
          )}

          {activeTab === 'integrations' && <BrowserAssistantAccess />}

          {/* connected_apps tab removed -- see 'integrations' tab */}

          {activeTab === 'domains' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-2">{t('settings.domainVerification')}</h2>
                <p className="text-sm text-muted-foreground mb-2">{t('settings.domainCustomHostingDesc')}</p>
                <p className="text-sm text-muted-foreground mb-4">{t('settings.domainVerificationDesc')}</p>
              </div>
              {domainError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {domainError}
                </div>
              )}

              {/* Domain Verification Section */}
              <div className="card p-4 @sm/main:p-6">
                <div className="flex flex-col @sm/main:flex-row @sm/main:items-center justify-between gap-3 mb-4">
                  <h2 className="text-lg font-semibold">{t('settings.domainVerification')}</h2>
                  <button
                    onClick={() => setShowAddDomain(!showAddDomain)}
                    className="btn-secondary flex items-center gap-2 text-sm w-full @sm/main:w-auto justify-center"
                  >
                    <Plus className="h-4 w-4" />
                    {t('settings.addDomain')}
                  </button>
                </div>
                
                <p className="text-sm text-muted-foreground mb-6">
                  {t('settings.domainVerificationDesc')}
                </p>
                
                {domainError && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {domainError}
                  </div>
                )}
                
                {showAddDomain && (
                  <form onSubmit={handleAddDomain} className="bg-muted/50 rounded-lg p-4 mb-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {t('settings.domainName')}
                      </label>
                      <input
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        placeholder={t('settings.domainPlaceholder')}
                        className="input w-full"
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('settings.domainNameHelp')}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {t('settings.verificationMethod')}
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setVerificationMethod('dns')}
                          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            verificationMethod === 'dns'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {t('settings.dnsTxtRecord')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setVerificationMethod('meta')}
                          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            verificationMethod === 'meta'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {t('settings.htmlMetaTag')}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowAddDomain(false)}
                        className="btn-secondary"
                      >
                        {t('actions.cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={addingDomain || !newDomain}
                        className="btn-primary flex items-center gap-2"
                      >
                        {addingDomain && <Spinner size="md" />}
                        {t('settings.startVerification')}
                      </button>
                    </div>
                  </form>
                )}
                
                {loadingDomains ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="xl" tone="muted" />
                  </div>
                ) : domainVerifications.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Link2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('settings.noDomains')}</p>
                    <p className="text-sm mt-1">{t('settings.addDomainToStart')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {domainVerifications.map((verification) => (
                      <div 
                        key={verification.id} 
                        className="border border-border rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(verification.status)}
                            <span className="font-medium">{verification.domain}</span>
                            {verification.channel && (
                              <Link 
                                href={`/c/${verification.channel.slug}`}
                                className="text-sm text-primary hover:underline flex items-center gap-1"
                              >
                                //{verification.channel.slug}
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {verification.status === 'pending' && (
                              <button
                                onClick={() => handleVerifyDomain(verification.id)}
                                disabled={verifyingDomainId === verification.id}
                                className="btn-secondary text-sm flex items-center gap-1"
                              >
                                {verifyingDomainId === verification.id ? (
                                  <Spinner size="xs" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                                {t('settings.checkVerification')}
                              </button>
                            )}
                            {/* A platform-issued app host is revoked by the
                                operator, never here; the API refuses the
                                delete as well, so hiding the control is the
                                honest state rather than a dead button. */}
                            {verification.platformZone ? (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Lock className="h-3 w-3" />
                                {t('settings.domainPlatformHost')}
                              </span>
                            ) : (
                              <button
                                onClick={() => handleDeleteDomain(verification.id)}
                                className="btn-ghost text-sm text-destructive hover:bg-destructive/10 p-2"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {verification.status === 'pending' && verification.verificationCode && (
                          <div className="bg-muted/50 rounded p-3 text-sm">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-muted-foreground">
                                {verification.method === 'dns' ? t('settings.addTxtRecordInstructions') : t('settings.addMetaTagInstructions')}
                              </span>
                              <button
                                onClick={() => copyToClipboard(
                                  verification.method === 'dns' 
                                    ? verification.verificationCode! 
                                    : `<meta name="robutler-verification" content="${verification.verificationCode}">`,
                                  verification.id
                                )}
                                className="btn-ghost text-xs flex items-center gap-1"
                              >
                                {copiedCode === verification.id ? (
                                  <>
                                    <Check className="h-3 w-3" />
                                    {t('settings.domainCopied')}
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-3 w-3" />
                                    {t('settings.domainCopy')}
                                  </>
                                )}
                              </button>
                            </div>
                            <code className="block bg-background px-3 py-2 rounded text-xs overflow-x-auto">
                              {verification.method === 'dns' 
                                ? verification.verificationCode 
                                : `<meta name="robutler-verification" content="${verification.verificationCode}">`
                              }
                            </code>
                            {verification.expiresAt && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {t('settings.expires')} {new Date(verification.expiresAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {verification.status === 'verified' && verification.verifiedAt && (
                          <p className="text-sm text-green-600">
                            {t('settings.verifiedOn')} {new Date(verification.verifiedAt).toLocaleDateString()}
                          </p>
                        )}

                        {/* Hosting DNS: the TXT record above proves ownership,
                            these records make the domain actually serve. A
                            platform-issued host resolves through the CDN
                            wildcard already, so it shows no records. */}
                        {verification.platformZone ? (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('settings.domainPlatformHostHelp')}
                          </p>
                        ) : (
                          renderDnsRecords(verification)
                        )}

                        {/* Path → app routing. Only offered once ownership is
                            proven; the API refuses bindings on an unverified
                            domain anyway. Read-only on a platform-issued host,
                            which serves exactly the app it was issued for. */}
                        {verification.status === 'verified' && (
                          <DomainAppBindings
                            verificationId={verification.id}
                            domain={verification.domain}
                            readOnly={!!verification.platformZone}
                          />
                        )}
                        
                        {verification.status === 'expired' && (
                          <p className="text-sm text-muted-foreground">
                            {t('settings.verificationExpiredMessage')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (<>
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">{t('settings.notificationSettings')}</h2>
              <div className="space-y-4">
                <label className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('settings.emailNotifications')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.emailNotificationsDesc')}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    className="h-5 w-5 rounded"
                  />
                </label>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('settings.pushNotifications')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.pushNotificationsDesc')}</div>
                    {pushPermissionState === 'denied' && (
                      <div className="text-xs text-red-500 mt-1">{t('settings.notificationsBlocked')}</div>
                    )}
                    {pushPermissionState === 'unsupported' && (
                      <div className="text-xs text-muted-foreground mt-1">{t('settings.pushNotSupported')}</div>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={pushNotifications}
                    onChange={(e) => handleTogglePush(e.target.checked)}
                    disabled={pushPermissionState === 'unsupported'}
                    className="h-5 w-5 rounded"
                  />
                </div>
                <label className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('settings.mentionNotifications')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.mentionNotificationsDesc')}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={mentionNotifications}
                    onChange={(e) => setMentionNotifications(e.target.checked)}
                    className="h-5 w-5 rounded"
                  />
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('settings.replyNotifications')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.replyNotificationsDesc')}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={replyNotifications}
                    onChange={(e) => setReplyNotifications(e.target.checked)}
                    className="h-5 w-5 rounded"
                  />
                </label>
                <button
                  onClick={handleSaveNotifications}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? <Spinner size="md" /> : saved ? <Check className="h-4 w-4" /> : null}
                  {saved ? t('actions.saved') : t('actions.saveChanges')}
                </button>
              </div>
            </div>

            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold mb-1">{t('settings.emailSettings')}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t('settings.emailSettingsDesc')}</p>
              <div className="space-y-4">
                {optionalEmailCategories.map((category) => (
                  <label key={category} className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium">{emailCategoryTitle(t, category)}</div>
                      <div className="text-sm text-muted-foreground">{emailCategoryDescription(t, category)}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={emailPreferences[category] !== false}
                      onChange={(e) => setEmailPreferences({ ...emailPreferences, [category]: e.target.checked })}
                      className="h-5 w-5 rounded shrink-0"
                    />
                  </label>
                ))}

                {essentialEmailCategories.length > 0 && (
                  <div className="border-t border-border pt-4 space-y-4">
                    {essentialEmailCategories.map((category) => (
                      <div key={category} className="flex items-center justify-between gap-4">
                        <div>
                          <div className="font-medium">{emailCategoryTitle(t, category)}</div>
                          <div className="text-sm text-muted-foreground">{emailCategoryDescription(t, category)}</div>
                        </div>
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
                          <Lock className="h-3.5 w-3.5" />
                          {t('settings.emailAlwaysOn')}
                        </span>
                      </div>
                    ))}
                    <p className="text-sm text-muted-foreground">{t('settings.emailAlwaysOnNote')}</p>
                  </div>
                )}

                <Link
                  href="/doc/privacy-policy"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t('settings.emailPrivacyLink')}
                </Link>

                {emailError && <p className="text-sm text-red-500">{emailError}</p>}

                <button
                  onClick={handleSaveNotifications}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? <Spinner size="md" /> : saved ? <Check className="h-4 w-4" /> : null}
                  {saved ? t('actions.saved') : t('actions.saveChanges')}
                </button>
              </div>
            </div>
          </>)}

          {activeTab === 'security' && (
            <PasskeyManagement />
          )}

          {activeTab === 'privacy' && (<>
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">{t('settings.privacySettings')}</h2>
              <div className="space-y-4">
                <label className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('settings.publicProfile')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.publicProfileDesc')}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={profilePublic}
                    onChange={(e) => setProfilePublic(e.target.checked)}
                    className="h-5 w-5 rounded"
                  />
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('settings.showOnlineStatus')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.showOnlineStatusDesc')}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={showOnlineStatus}
                    onChange={(e) => setShowOnlineStatus(e.target.checked)}
                    className="h-5 w-5 rounded"
                  />
                </label>
                <div>
                  <div className="font-medium">{t('settings.allowDMs')}</div>
                  <div className="text-sm text-muted-foreground mb-3">{t('settings.allowDMsDesc')}</div>
                  <div className="space-y-3">
                    {([
                      { value: 'everyone' as const, labelKey: 'settings.dmEveryone', descKey: 'settings.dmEveryoneDesc' },
                      { value: 'following' as const, labelKey: 'settings.dmFollowing', descKey: 'settings.dmFollowingDesc' },
                      { value: 'nobody' as const, labelKey: 'settings.dmNobody', descKey: 'settings.dmNobodyDesc' },
                    ]).map(opt => (
                      <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="dmPolicy"
                          value={opt.value}
                          checked={dmPolicy === opt.value}
                          onChange={() => setDmPolicy(opt.value)}
                          className="mt-1 h-4 w-4 accent-primary"
                        />
                        <div>
                          <div className="font-medium">{t(opt.labelKey)}</div>
                          <div className="text-sm text-muted-foreground">{t(opt.descKey)}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleSavePrivacy}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? <Spinner size="md" /> : saved ? <Check className="h-4 w-4" /> : null}
                  {saved ? t('actions.saved') : t('actions.saveChanges')}
                </button>

                {/* Cookie choices live in the consent sheet, not as another
                    toggle here, so there is exactly one place they are set.
                    GDPR Art. 7(3) requires withdrawal to be as easy as
                    consent, which needs a route back after the first choice. */}
                <div className="flex items-center justify-between border-t border-border pt-4">
                  <div>
                    <div className="font-medium">{t('cookies.preferences')}</div>
                    <div className="text-sm text-muted-foreground">{t('cookies.description')}</div>
                  </div>
                  <button
                    type="button"
                    onClick={openCookiePreferences}
                    className="btn-secondary flex items-center gap-2 shrink-0"
                  >
                    {t('cookies.settings')}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold mb-1">{t('settings.nsfwContent')}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t('settings.nsfwDescription')}</p>
              <div className="space-y-3">
                {([
                  { value: 'mask' as const, labelKey: 'settings.nsfwBlur', descKey: 'settings.nsfwBlurDesc' },
                  { value: 'filter' as const, labelKey: 'settings.nsfwHide', descKey: 'settings.nsfwHideDesc' },
                  { value: 'allow' as const, labelKey: 'settings.nsfwShow', descKey: 'settings.nsfwShowDesc' },
                ]).map(opt => (
                  <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="nsfwMode"
                      value={opt.value}
                      checked={nsfwMode === opt.value}
                      onChange={() => setNsfwMode(opt.value)}
                      className="mt-1 h-4 w-4 accent-primary"
                    />
                    <div>
                      <div className="font-medium">{t(opt.labelKey)}</div>
                      <div className="text-sm text-muted-foreground">{t(opt.descKey)}</div>
                    </div>
                  </label>
                ))}
              </div>
              <button
                onClick={async () => {
                  setNsfwSaving(true);
                  try {
                    const res = await fetch('/api/users/me', {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ preferences: { nsfwMode } }),
                    });
                    if (res.ok) {
                      await refresh();
                      setSaved(true);
                      setTimeout(() => setSaved(false), 2000);
                    }
                  } catch (e) {
                    console.error('Error saving NSFW preference:', e);
                  } finally {
                    setNsfwSaving(false);
                  }
                }}
                disabled={nsfwSaving || nsfwMode === currentNsfwMode}
                className="btn-primary flex items-center gap-2 mt-4"
              >
                {nsfwSaving ? <Spinner size="md" /> : null}
                {saved ? t('actions.saved') : t('actions.saveChanges')}
              </button>
            </div>

            {/* Your data: GDPR Arts. 15/17/20 self-serve surfaces. Export is a
                plain download; deletion is request + 30-day grace + cancel,
                backed by /api/account/deletion (see lib/account/deletion.ts
                and docs/internal/launch/account-deletion-map.md). */}
            <YourDataCard t={t} />
          </>)}

          {/* AGENTS tab — hidden (see the `tabs` array above). Kept commented
              rather than deleted so restoring it is a one-line revert.
          {activeTab === 'agents' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">{t('settings.agentsSection')}</h2>
                <p className="text-sm text-muted-foreground mb-4">{t('settings.agentsSectionDesc')} <a href="/develop/webagents/cli" className="text-primary hover:underline">{t('settings.cliGuide')}</a></p>
              </div>

              <div className="card p-4 @sm/main:p-6 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold">{t('settings.connectLocalAgent')}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{t('settings.connectLocalAgentDesc')}</p>
                </div>
                <a href="/develop/webagents/cli" target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm flex items-center gap-1.5 shrink-0">
                  <BookOpen className="h-3.5 w-3.5" />
                  {t('settings.viewDocs')}
                </a>
              </div>
            </div>
          )}
          */}

          {/* AI MODELS tab (BYOK) — hidden (see the `tabs` array above). Kept
              commented rather than deleted so restoring it is a one-line revert.
          {activeTab === 'ai_models' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">{t('settings.aiModelsSection')}</h2>
                <p className="text-sm text-muted-foreground mb-4">{t('settings.aiModelsSectionDesc')} <a href="/develop/webagents/payments/spending-limits" className="text-primary hover:underline">{t('settings.howApiKeysWork')}</a></p>
              </div>
              <div className="card p-4 @sm/main:p-6">
                <h3 className="text-base font-semibold mb-2">{t('settings.providerKeysTitle')}</h3>
                <p className="text-sm text-muted-foreground mb-4">{t('settings.providerKeysDesc')}</p>
                {apiKeys.filter((t: any) => t.type === 'provider').length > 0 && (
                  <div className="space-y-2 mb-4">
                    {apiKeys.filter((t: any) => t.type === 'provider').map((token: any) => {
                      // Same columns and unit as the platform keys below: credits, never a `$`.
                      const fmt = formatNanocredits;
                      const spentToday = token.spentToday ?? '0';
                      const spentTotal = token.spentTotal ?? '0';
                      const limitDaily = token.limitDaily ?? null;
                      const limitTotal = token.limitTotal ?? null;
                      return (
                      <div key={token.id} className="flex items-center justify-between border border-border rounded-lg p-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium uppercase bg-muted px-2 py-0.5 rounded">{token.provider}</span>
                            <span className="text-sm">{token.name}</span>
                            <span className="text-xs text-muted-foreground">{token.keyPrefix}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                            <span>{fmt(spentToday)} {t('settings.todayLabel')}{limitDaily ? ` / ${fmt(limitDaily)} ${t('settings.limitLabel')}` : ''}</span>
                            <span>{fmt(spentTotal)} {t('settings.totalUsedLabel')}{limitTotal ? ` / ${fmt(limitTotal)} ${t('settings.limitLabel')}` : ''}</span>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            await fetch(`/api/access-tokens/${token.id}`, { method: 'DELETE' });
                            fetchApiKeys();
                          }}
                          className="text-destructive hover:bg-destructive/10 p-1 rounded shrink-0"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );})}
                  </div>
                )}
                <button onClick={() => setShowAddProviderKey(true)} className="btn-primary flex items-center gap-2 text-sm">
                  <Plus className="h-4 w-4" />
                  {t('settings.addProviderKey')}
                </button>
                <AddProviderKeyDialog open={showAddProviderKey} onClose={() => setShowAddProviderKey(false)} onAdded={() => { setShowAddProviderKey(false); fetchApiKeys(); }} />
              </div>

              {apiKeys.filter((t: any) => t.type === 'provider').length > 0 && (
                <div className="card p-4 @sm/main:p-6 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium">{t('settings.allowModelSubstitution')}</h3>
                      <p className="text-xs text-muted-foreground mt-1 max-w-md">
                        {t('settings.allowModelSubstitutionDesc')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const newVal = !allowByokSubstitution;
                        setAllowByokSubstitution(newVal);
                        try {
                          await fetch('/api/settings', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ allowByokSubstitution: newVal }) });
                        } catch {}
                      }}
                      className={cn(
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                        allowByokSubstitution ? 'bg-primary' : 'bg-muted',
                      )}
                    >
                      <span className={cn(
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-lg ring-0 transition-transform',
                        allowByokSubstitution ? 'translate-x-5' : 'translate-x-0',
                      )} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          */}

          {/* DEVELOPER tab */}
          {activeTab === 'developer' && (() => {
            const nonProvider = apiKeys.filter((t: any) => t.type !== 'provider');
            const filtered = tokenFilter === 'active'
              ? nonProvider.filter((t: any) => t.isActive && !t.revokedAt)
              : nonProvider;
            const totalPages = Math.max(1, Math.ceil(filtered.length / TOKENS_PER_PAGE));
            const page = Math.min(tokenPage, totalPages - 1);
            const paged = filtered.slice(page * TOKENS_PER_PAGE, (page + 1) * TOKENS_PER_PAGE);
            const activeCount = nonProvider.filter((t: any) => t.isActive && !t.revokedAt).length;
            const revokedCount = nonProvider.length - activeCount;

            return (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">{t('settings.developerSection')}</h2>
                <p className="text-sm text-muted-foreground mb-4">{t('settings.developerSectionDesc')} <a href="/develop/webagents/api/platform" className="text-primary hover:underline">{t('settings.apiReferenceLink')}</a></p>
              </div>

              {/* OAuth Apps */}
              <OAuthAppsCard />

              <div className="card p-4 @sm/main:p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold">{t('settings.apiKeysSection')}</h3>
                  <button
                    onClick={() => setShowCreateKey(!showCreateKey)}
                    className="btn-primary flex items-center gap-1.5 text-sm"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('settings.newKeyBtn')}
                  </button>
                </div>

                {/* Filter bar */}
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => { setTokenFilter('active'); setTokenPage(0); }}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${tokenFilter === 'active' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                  >
                    {t('settings.activeCountLabel', { count: activeCount })}
                  </button>
                  <button
                    onClick={() => { setTokenFilter('all'); setTokenPage(0); }}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${tokenFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                  >
                    {t('settings.allCountLabel', { count: nonProvider.length })}
                  </button>
                  {revokedCount > 0 && tokenFilter === 'active' && (
                    <span className="text-[11px] text-muted-foreground ml-1">{t('settings.revokedHidden', { count: revokedCount })}</span>
                  )}
                </div>

                {showCreateKey && (
                  <div className="border border-border rounded-lg p-4 mb-4 space-y-3">
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder={t('settings.keyNameInputPlaceholder')}
                      className="input w-full"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          if (!newKeyName.trim()) return;
                          setCreatingKey(true);
                          try {
                            const res = await fetch('/api/access-tokens', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ type: 'api_key', name: newKeyName.trim() }),
                            });
                            if (res.ok) {
                              const data = await res.json();
                              if (data.rawKey) setNewlyCreatedKey(data.rawKey);
                              setNewKeyName('');
                              setShowCreateKey(false);
                              fetchApiKeys();
                            }
                          } finally {
                            setCreatingKey(false);
                          }
                        }}
                        disabled={creatingKey || !newKeyName.trim()}
                        className="btn-primary text-sm"
                      >
                        {creatingKey ? t('settings.creatingOauthApp') : t('settings.createKeyBtn')}
                      </button>
                      <button onClick={() => setShowCreateKey(false)} className="btn-secondary text-sm">{t('actions.cancel')}</button>
                    </div>
                  </div>
                )}

                {newlyCreatedKey && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">{t('settings.keyCreatedMsg')}</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-background px-3 py-2 rounded text-xs font-mono break-all">{newlyCreatedKey}</code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(newlyCreatedKey); setCopiedApiKey(true); setTimeout(() => setCopiedApiKey(false), 2000); }}
                        className="btn-secondary p-2"
                      >
                        {copiedApiKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                    <button onClick={() => setNewlyCreatedKey(null)} className="text-xs text-muted-foreground mt-2 hover:underline">{t('actions.dismiss')}</button>
                  </div>
                )}

                {loadingApiKeys ? (
                  <div className="flex justify-center py-8"><Spinner size="lg" tone="muted" /></div>
                ) : paged.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">
                    {tokenFilter === 'active' ? t('settings.noActiveKeys') : t('settings.noKeysYet')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {paged.map((token: any) => {
                      const isActive = token.isActive && !token.revokedAt;
                      const isEditing = editingTokenId === token.id;
                      const isConfirmingRevoke = confirmRevokeTokenId === token.id;
                      return (
                        <div key={token.id} className={`border border-border rounded-lg p-3 ${!isActive ? 'opacity-50' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`text-[10px] font-medium uppercase px-1.5 py-0.5 rounded ${
                                token.type === 'api_key' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                token.type === 'oauth_app' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                token.type === 'agent' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                              }`}>
                                {token.type === 'api_key' ? t('settings.badgeKey') : token.type === 'oauth_app' ? t('settings.badgeApp') : token.type === 'agent' ? t('settings.badgeAgent') : token.type}
                              </span>
                              <span className="font-medium text-sm truncate">{token.name}</span>
                              {token.keyPrefix && <span className="text-xs text-muted-foreground">{token.keyPrefix}</span>}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className={`text-xs ${isActive ? 'text-green-600' : 'text-red-500'}`}>
                                {isActive ? t('settings.active') : t('settings.revoked')}
                              </span>
                              {isActive && !isConfirmingRevoke && (
                                <>
                                  <Tip label={t('settings.editLimits')}>
                                    <button
                                      onClick={() => {
                                        setEditingTokenId(isEditing ? null : token.id);
                                        if (!isEditing) {
                                          setEditTokenDaily(token.limitDaily ? (Number(token.limitDaily) / 1e9).toFixed(2) : '');
                                          setEditTokenTotal(token.limitTotal ? (Number(token.limitTotal) / 1e9).toFixed(2) : '');
                                        }
                                      }}
                                      className="text-primary hover:bg-primary/10 px-2 py-0.5 rounded text-xs"
                                    >
                                      {t('settings.editLimits')}
                                    </button>
                                  </Tip>
                                  <Tip label={t('settings.revokeConfirm')}>
                                    <button
                                      onClick={() => setConfirmRevokeTokenId(token.id)}
                                      className="text-destructive hover:bg-destructive/10 p-1 rounded"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </Tip>
                                </>
                              )}
                              {isConfirmingRevoke && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-destructive">{t('settings.revokeConfirm')}</span>
                                  <button
                                    onClick={async () => {
                                      await fetch(`/api/access-tokens/${token.id}`, { method: 'DELETE' });
                                      setConfirmRevokeTokenId(null);
                                      fetchApiKeys();
                                    }}
                                    className="text-xs bg-destructive text-destructive-foreground px-2 py-0.5 rounded hover:bg-destructive/90"
                                  >
                                    {t('settings.yesBtn')}
                                  </button>
                                  <button
                                    onClick={() => setConfirmRevokeTokenId(null)}
                                    className="text-xs btn-secondary px-2 py-0.5"
                                  >
                                    {t('settings.noBtn')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Spending limits display */}
                          {!isEditing && (token.limitDaily || token.limitTotal) && (
                            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                              {token.limitDaily && (
                                <span>{t('settings.dailyUsed')}<span className="font-medium text-foreground/70">{formatNanocredits(token.limitDaily)}</span> ({t('settings.usedSuffix')} {formatNanocredits(token.spentToday)})</span>
                              )}
                              {token.limitTotal && (
                                <span>{t('settings.totalUsed')}<span className="font-medium text-foreground/70">{formatNanocredits(token.limitTotal)}</span> ({t('settings.usedSuffix')} {formatNanocredits(token.spentTotal)})</span>
                              )}
                            </div>
                          )}

                          {/* Edit limits inline form */}
                          {isEditing && (
                            <div className="flex items-end gap-2 mt-2">
                              <div className="flex-1">
                                <label className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('settings.dailyCreditsLabel')}</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editTokenDaily}
                                  onChange={(e) => setEditTokenDaily(e.target.value)}
                                  className="input w-full text-xs py-1 px-2 mt-0.5"
                                  placeholder="1.00"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('settings.totalCreditsLabel')}</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editTokenTotal}
                                  onChange={(e) => setEditTokenTotal(e.target.value)}
                                  className="input w-full text-xs py-1 px-2 mt-0.5"
                                  placeholder="5.00"
                                />
                              </div>
                              <button
                                disabled={savingTokenLimits}
                                onClick={async () => {
                                  setSavingTokenLimits(true);
                                  try {
                                    // Credits typed -> nanocredits stored, at the ledger's 1e9 (see formatNanocredits).
                                    const dailyNano = editTokenDaily ? Math.round(parseFloat(editTokenDaily) * 1e9).toString() : null;
                                    const totalNano = editTokenTotal ? Math.round(parseFloat(editTokenTotal) * 1e9).toString() : null;
                                    const res = await fetch(`/api/access-tokens/${token.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ limitDaily: dailyNano, limitTotal: totalNano }),
                                    });
                                    if (res.ok) {
                                      fetchApiKeys();
                                      setEditingTokenId(null);
                                    }
                                  } finally {
                                    setSavingTokenLimits(false);
                                  }
                                }}
                                className="btn-primary text-xs py-1 px-3"
                              >
                                {savingTokenLimits ? <Spinner size="xs" /> : t('actions.save')}
                              </button>
                              <button onClick={() => setEditingTokenId(null)} className="btn-secondary text-xs py-1 px-3">{t('actions.cancel')}</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {t('settings.pageOfKeys', { page: page + 1, total: totalPages, count: filtered.length })}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setTokenPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        className="btn-secondary text-xs px-2 py-1 disabled:opacity-40"
                      >
                        {t('settings.prevPage')}
                      </button>
                      <button
                        onClick={() => setTokenPage(Math.min(totalPages - 1, page + 1))}
                        disabled={page >= totalPages - 1}
                        className="btn-secondary text-xs px-2 py-1 disabled:opacity-40"
                      >
                        {t('settings.nextPage')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* VOICE & AUDIO tab */}
          {activeTab === 'voice_audio' && (
            <div className="card p-4 @sm/main:p-6">
              <h2 className="text-lg font-semibold mb-2">{t('settings.voiceAndAudio')}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t('settings.voiceDesc')}</p>
                <div className="space-y-5">
                  <label className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{t('settings.autoSendOnSilence')}</div>
                      <div className="text-xs text-muted-foreground">{t('settings.autoSendDesc')}</div>
                    </div>
                  <input type="checkbox" checked={voiceAutoSend} onChange={(e) => setVoiceAutoSend(e.target.checked)} className="h-5 w-5 rounded" />
                  </label>
                  <label className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{t('settings.trimSilence')}</div>
                      <div className="text-xs text-muted-foreground">{t('settings.trimSilenceDesc')}</div>
                    </div>
                  <input type="checkbox" checked={voiceTrimSilence} onChange={(e) => setVoiceTrimSilence(e.target.checked)} className="h-5 w-5 rounded" />
                  </label>
                  <div>
                  <label className="block text-sm font-medium mb-1">{t('settings.silenceSensitivity')}</label>
                  <p className="text-xs text-muted-foreground mb-2">{t('settings.silenceSensitivityDesc')}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-8">{t('settings.silenceSensitivityLow')}</span>
                    <input type="range" min={0.005} max={0.05} step={0.005} value={voiceSilenceThreshold} onChange={(e) => setVoiceSilenceThreshold(parseFloat(e.target.value))} className="flex-1 accent-primary" />
                      <span className="text-xs text-muted-foreground w-8">{t('settings.silenceSensitivityHigh')}</span>
                      <span className="text-xs font-mono text-muted-foreground w-10 text-right">{voiceSilenceThreshold.toFixed(3)}</span>
                    </div>
                  </div>
                  <div>
                  <label className="block text-sm font-medium mb-1">{t('settings.silenceWaitTime')}</label>
                  <p className="text-xs text-muted-foreground mb-2">{t('settings.silenceWaitTimeDesc')}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-10">500ms</span>
                    <input type="range" min={500} max={3000} step={100} value={voiceSilenceDuration} onChange={(e) => setVoiceSilenceDuration(parseInt(e.target.value))} className="flex-1 accent-primary" />
                      <span className="text-xs text-muted-foreground w-10">3000ms</span>
                      <span className="text-xs font-mono text-muted-foreground w-14 text-right">{voiceSilenceDuration}ms</span>
                    </div>
                  </div>
                  <div>
                  <label className="block text-sm font-medium mb-1">{t('settings.minRecordingDuration')}</label>
                  <p className="text-xs text-muted-foreground mb-2">{t('settings.minRecordingDurationDesc')}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-10">200ms</span>
                    <input type="range" min={200} max={2000} step={100} value={voiceMinRecording} onChange={(e) => setVoiceMinRecording(parseInt(e.target.value))} className="flex-1 accent-primary" />
                      <span className="text-xs text-muted-foreground w-10">2000ms</span>
                      <span className="text-xs font-mono text-muted-foreground w-14 text-right">{voiceMinRecording}ms</span>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      setSaving(true);
                      try {
                        const res = await fetch('/api/users/me/settings', {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ voiceSilenceThreshold, voiceSilenceDuration, voiceMinRecording, voiceAutoSend, voiceTrimSilence }),
                      });
                      localStorage.setItem('voiceSettings', JSON.stringify({ silenceThreshold: voiceSilenceThreshold, silenceDuration: voiceSilenceDuration, minRecordingDuration: voiceMinRecording, autoSend: voiceAutoSend, trimSilence: voiceTrimSilence }));
                      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
                    } catch (error) { console.error('Error saving voice settings:', error); } finally { setSaving(false); }
                    }}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2 text-sm"
                  >
                    {saving ? <Spinner size="md" /> : saved ? <Check className="h-4 w-4" /> : null}
                    {saved ? t('settings.voiceSaved') : t('settings.saveVoiceSettings')}
                  </button>
                </div>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className="card p-6">
              <h2 className="mb-5 text-lg font-semibold">{t('settings.appearance')}</h2>
              <div className="flex flex-col gap-8">
                <SettingSection title={t('settings.theme')}>
                  <SettingRow
                    label={t('settings.theme')}
                    control={
                      <Segmented
                        value={theme as 'light' | 'dark' | 'system'}
                        ariaLabel={t('settings.theme')}
                        onChange={(v) => setTheme(v)}
                        options={[
                          { value: 'light', label: t('settings.light'), icon: Sun },
                          { value: 'dark', label: t('settings.dark'), icon: Moon },
                          { value: 'system', label: t('settings.system'), icon: Monitor },
                        ]}
                      />
                    }
                  />
                  <SettingRow
                    htmlFor="setting-autohide-rail"
                    label={t('settings.autoHideRail')}
                    description={t('settings.autoHideRailDesc')}
                    control={
                      <Toggle
                        id="setting-autohide-rail"
                        checked={railAutohide}
                        onChange={setRailAutohide}
                        label={t('settings.autoHideRail')}
                      />
                    }
                  />
                </SettingSection>

                {/* "Opening apps" ("Where new apps open": on a new screen, or
                    on the current one) is retired, 2026-09-29: every app opens
                    on top of the screen you are on (the Stage algorithm, open
                    question 1, answered with the proposal). A person who wants
                    a screen of its own makes one in Stage. */}

                <p className="px-1 text-[12px] text-muted-foreground">
                  {t('settings.savedOnThisDevice')}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'language' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">{t('settings.language')}</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 @sm/main:grid-cols-3 gap-2">
                  {supportedLocales.map((loc) => (
                    <button
                      key={loc}
                      onClick={() => setLocale(loc)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        locale === loc
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-accent'
                      }`}
                    >
                      {localeNames[loc]}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('settings.languagePreferenceSaved')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PasskeyManagement() {
  const { t } = useTranslation();
  const [passkeys, setPasskeys] = useState<{ id: string; deviceType: string | null; backedUp: boolean | null; createdAt: string; lastUsedAt: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchPasskeys = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/webauthn/passkeys');
      if (res.ok) {
        const data = await res.json();
        setPasskeys(data.passkeys || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPasskeys(); }, [fetchPasskeys]);

  const handleRegister = async () => {
    setRegistering(true);
    setError(null);
    setSuccess(null);
    try {
      const { startRegistration } = await import('@simplewebauthn/browser');
      const optionsRes = await fetch('/api/auth/webauthn/register');
      if (!optionsRes.ok) {
        const text = await optionsRes.text();
        let msg = t('settings.passkeyServerError', { status: optionsRes.status });
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      const options = await optionsRes.json();
      const regResp = await startRegistration({ optionsJSON: options });
      const verifyRes = await fetch('/api/auth/webauthn/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regResp),
      });
      const result = await verifyRes.json();
      if (result.verified) {
        setSuccess(t('settings.passkeyAdded'));
        try { localStorage.setItem('robutler_passkey_registered', '1'); } catch {}
        fetchPasskeys();
      } else {
        throw new Error(result.error || t('settings.passkeyRegistrationFailed'));
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError(t('settings.passkeyRegistrationCancelled'));
      } else {
        setError(err.message || t('settings.failedToRegisterPasskey'));
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('settings.removePasskeyConfirm'))) return;
    setDeletingId(id);
    try {
      const res = await fetch('/api/auth/webauthn/passkeys', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      if (res.ok) {
        setPasskeys(prev => prev.filter(p => p.id !== id));
        setSuccess(t('settings.passkeyRemoved'));
      }
    } catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  return (
    <div className="card p-4 @sm/main:p-6">
      <div className="flex flex-col gap-3 @sm/main:flex-row @sm/main:items-center @sm/main:justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">{t('settings.passkeysTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('settings.passkeysDescription')}</p>
        </div>
        <button
          onClick={handleRegister}
          disabled={registering}
          className="btn-primary flex items-center justify-center gap-1.5 text-sm shrink-0 w-full @sm/main:w-auto"
        >
          {registering ? <Spinner size="sm" /> : <Plus className="h-3.5 w-3.5" />}
          {t('settings.addPasskey')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 text-sm flex items-center gap-2">
          <Check className="h-4 w-4 shrink-0" />
          {success}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><Spinner size="lg" tone="muted" /></div>
      ) : passkeys.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">
          <Fingerprint className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p>{t('settings.noPasskeys')}</p>
          <p className="mt-1">{t('settings.noPasskeysDesc')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {passkeys.map(pk => (
            <div key={pk.id} className="flex items-center justify-between border border-border rounded-lg p-3">
              <div className="flex items-center gap-3 min-w-0">
                <Fingerprint className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {pk.deviceType === 'multiDevice' ? t('settings.syncedPasskey') : t('settings.deviceBoundPasskey')}
                    {pk.backedUp && <span className="text-xs text-muted-foreground ml-1.5">{t('settings.backedUp')}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('settings.addedPrefix')}{new Date(pk.createdAt).toLocaleDateString()}
                    {pk.lastUsedAt && <>{t('settings.lastUsedPasskey')}{new Date(pk.lastUsedAt).toLocaleDateString()}</>}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDelete(pk.id)}
                disabled={deletingId === pk.id}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"
              >
                {deletingId === pk.id ? <Spinner size="md" /> : <Trash2 className="h-4 w-4" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Core settings view. Used both as the standalone `/settings` page and
 * embedded inside the workspace's full-screen dialog system. When
 * `embedded` is true we skip the page's outer chrome
 * (`max-w-4xl mx-auto`, `PanelBackground`, `<h1>`) so the dialog frame
 * supplies its own header without duplication.
 *
 * `initialTab` exists because reading `window.location.search` here is not
 * reliable when embedded. `/settings?tab=domains` reaches the canvas as
 * `/workspace/<id>?dialog=settings&tab=domains`, and the deep-link consumer
 * strips both params via `history.replaceState` BEFORE this component mounts —
 * so by the time the initializer below runs, the query is already gone and
 * every deep link silently landed on Profile (found 2026-08-05). The dialog
 * registry passes the tab down explicitly instead; the URL read stays as the
 * fallback for the standalone page.
 */
export function SettingsView({
  embedded = false,
  initialTab,
}: { embedded?: boolean; initialTab?: string } = {}) {
  return (
    <Suspense fallback={null}>
      <SettingsContent embedded={embedded} initialTab={initialTab} />
    </Suspense>
  );
}

/**
 * "Your data" card (Settings -> Privacy): self-serve export (GDPR Arts. 15/20)
 * and account deletion with a 30-day grace window (Art. 17). Deletion is a
 * two-step confirm and shows the pending state with a cancel path, because
 * withdrawal of the request must be as easy as making it.
 */
function YourDataCard({ t }: { t: (key: string) => string }) {
  const [status, setStatus] = useState<{
    requestedAt: string | null;
    purgeAfter: string | null;
    /** Legacy name of the same number as `earningsCredits`; read the credits one. */
    earningsDollars?: number;
    earningsCredits?: number;
  } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch('/api/account/deletion', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => {});
  }, []);

  const requestDeletion = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/account/deletion', { method: 'POST', credentials: 'same-origin' });
      if (r.ok) setStatus(await r.json());
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const cancelDeletion = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/account/deletion', { method: 'DELETE', credentials: 'same-origin' });
      if (r.ok) setStatus({ requestedAt: null, purgeAfter: null });
    } finally {
      setBusy(false);
    }
  };

  const pending = !!status?.requestedAt;

  return (
    <div className="card p-6 mt-6">
      <h2 className="text-lg font-semibold mb-1">{t('settings.yourData')}</h2>
      <p className="text-sm text-muted-foreground mb-4">{t('settings.yourDataDesc')}</p>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">{t('settings.exportData')}</div>
            <div className="text-sm text-muted-foreground">{t('settings.exportDataDesc')}</div>
          </div>
          <a href="/api/account/export" download className="btn-secondary shrink-0">
            {t('settings.exportDataButton')}
          </a>
        </div>

        <div className="border-t border-border pt-4">
          {pending ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium text-destructive">{t('settings.deletionPending')}</div>
                <div className="text-sm text-muted-foreground">
                  {t('settings.deletionPendingDesc')}{' '}
                  {status?.purgeAfter ? new Date(status.purgeAfter).toLocaleDateString() : ''}
                </div>
              </div>
              <button onClick={cancelDeletion} disabled={busy} className="btn-secondary shrink-0">
                {t('settings.deletionCancel')}
              </button>
            </div>
          ) : confirming ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium text-destructive">{t('settings.deleteAccountConfirmTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('settings.deleteAccountConfirmDesc')}</div>
                {/* Unwithdrawn Creator Rewards become unreachable once the
                    account is purged. Warn while the choice is still open,
                    rather than leaving the money to vanish quietly. */}
                {(status?.earningsCredits ?? status?.earningsDollars ?? 0) > 0 && (
                  <div className="mt-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    {t('settings.deleteAccountEarningsWarning').replace(
                      '{amount}',
                      // Credits, as the wallet's rewards card shows this same balance.
                      formatCredits(status?.earningsCredits ?? status?.earningsDollars ?? 0),
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setConfirming(false)} className="btn-secondary">
                  {t('actions.cancel')}
                </button>
                <button
                  onClick={requestDeletion}
                  disabled={busy}
                  className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
                >
                  {t('settings.deleteAccountConfirmButton')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">{t('settings.deleteAccount')}</div>
                <div className="text-sm text-muted-foreground">{t('settings.deleteAccountDesc')}</div>
              </div>
              <button onClick={() => setConfirming(true)} className="btn-secondary text-destructive shrink-0">
                {t('settings.deleteAccountButton')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
